'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { CalendarDays, Loader2, UserRound } from 'lucide-react';
import { WebinarPublicInfo, fetchWebinarPublicInfo, formatSessionDate } from '@/lib/webinar/webinarService';
import WebinarForm from './WebinarForm';

const OUTCOMES = [
  'Discover the four foundations of the CARE Framework and why they are essential to building a successful, sustainable and well-led care business.',
  'Understand the difference between starting a care business and running one successfully, including the responsibilities and challenges that many aspiring providers underestimate.',
  'Recognise the common mistakes that can undermine care businesses, from ineffective leadership and poor decision-making to weak governance and operational systems.',
  'Gain practical insights from real-world social care experience and develop a clearer understanding of what it takes to build a business that delivers quality care while remaining commercially sustainable.',
];

export default function WebinarView() {
  const [info, setInfo] = useState<WebinarPublicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const data = await fetchWebinarPublicInfo();
        if (!cancelled) setInfo(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Webinar details could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const open = info?.active === true && (info?.upcoming?.length ?? 0) > 0;

  return (
    <>
      <section className="bg-[#581C32] text-white">
        <div className="mx-auto w-full max-w-[860px] px-4 py-10 sm:py-14">
          <p className="inline-block rounded-full border border-[#C6A667] px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#C6A667]">
            FREE LIVE MASTERCLASS
          </p>
          {loading ? (
            <div className="mt-6 space-y-3" aria-busy="true">
              <div className="h-9 w-3/4 animate-pulse rounded bg-white/15" />
              <div className="h-5 w-full animate-pulse rounded bg-white/10" />
              <div className="h-5 w-2/3 animate-pulse rounded bg-white/10" />
            </div>
          ) : (
            <>
              <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
                {info?.title || 'Free weekly masterclass'}
              </h1>
              <p className="mt-4 text-sm font-semibold text-[#C6A667] sm:text-base">
                Every Wednesday · 6:30pm to 7:15pm (UK time) · Live on Zoom · 45 minutes
              </p>
              {info?.shortDescription && (
                <p className="mt-4 text-base leading-relaxed text-white/90 sm:text-lg">{info.shortDescription}</p>
              )}
              {open && (
                <p className="mt-5 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3.5 py-2 text-sm font-semibold sm:text-base">
                  <CalendarDays size={18} className="text-[#C6A667]" aria-hidden="true" />
                  Next session: {formatSessionDate(info?.upcoming?.[0])}
                </p>
              )}
            </>
          )}
        </div>
      </section>

      <section className="bg-[#F8F3E9]">
        <div className="mx-auto grid w-full max-w-[860px] gap-6 px-4 py-8 sm:py-12">
          {error && (
            <p role="alert" className="rounded-lg bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {error}
            </p>
          )}

          <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-8">
            <h2 className="text-xl font-extrabold text-[#581C32] sm:text-2xl">In this masterclass you will:</h2>
            <ul className="mt-4 space-y-3">
              {OUTCOMES.map((o) => (
                <li key={o} className="flex items-start gap-3 text-base text-[#2B0D18]">
                  <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-[#C6A667]" aria-hidden="true" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-8">
            <h2 className="text-xl font-extrabold text-[#581C32] sm:text-2xl">Your host</h2>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
              <div
                className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-full border-2 border-[#C6A667] bg-[#F8F3E9] text-[#581C32]"
                role="img"
                aria-label="Photo of Linda Dube to be added"
              >
                <UserRound size={40} aria-hidden="true" />
              </div>
              <div>
                <p className="text-lg font-bold text-[#581C32]">Linda Dube</p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-[#C6A667]">
                  Care Business Coach · Social Care Entrepreneur · Registered Social Worker and Best Interests Assessor · Trainer
                </p>
                <p className="mt-3 text-base leading-relaxed text-[#2B0D18]">
                  Linda Dube is a Care Business Coach, Social Care Entrepreneur, Registered Social Worker and Best
                  Interests Assessor, and Trainer, with over 20 years of experience in adult social care. In 2023 she
                  founded TLS Social Care Ltd, a CQC-registered domiciliary care and supported living provider in
                  Leeds, and grew it to a six-figure turnover and more than 40 staff. She created Care Business School
                  to give aspiring and existing care business owners the honest, practical guidance she wishes she had
                  when she started.
                </p>
              </div>
            </div>
          </div>

          <div id="register" className="scroll-mt-24 rounded-2xl bg-white p-5 shadow-sm sm:p-8">
            <h2 className="text-xl font-extrabold text-[#581C32] sm:text-2xl">Register free</h2>
            {loading ? (
              <p className="mt-4 text-sm text-[#2B0D18]/70">
                <Loader2 size={16} className="mr-2 inline animate-spin" aria-hidden="true" />
                Loading…
              </p>
            ) : open ? (
              <div className="mt-4">
                <WebinarForm upcoming={info?.upcoming ?? []} />
              </div>
            ) : (
              <p className="mt-4 text-base leading-relaxed text-[#2B0D18]">
                Registrations are currently closed. Join our{' '}
                <Link href="/subscribe" className="font-semibold text-[#581C32] underline underline-offset-2">
                  mailing list
                </Link>{' '}
                to hear about the next masterclass.
              </p>
            )}
            <p className="mt-5 border-t border-[#581C32]/10 pt-4 text-sm text-[#2B0D18]">
              Attended before?{' '}
              <Link href="/review?context=webinar" className="font-semibold text-[#581C32] underline underline-offset-2">
                Leave a review
              </Link>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
